import { createContext, ComponentChildren } from "preact";
import { useContext, useState } from "preact/hooks";
import { JSX } from "preact/jsx-runtime";
import {
  NotificationContainer,
  NotificationItem,
  NotificationType,
} from "../ui/Notification";

interface NotificationContextType {
  notifications: NotificationItem[];
  showNotification: (
    type: NotificationType,
    message: string | JSX.Element,
    duration?: number,
    slotId?: 1 | 2
  ) => void;
  showError: (message: string, duration?: number) => void;
  showSuccess: (
    message: string | JSX.Element,
    duration?: number,
    slotId?: 1 | 2
  ) => void;
  showInfo: (message: string, duration?: number) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: ComponentChildren;
  renderContainer?: boolean;
}

export const NotificationProvider = ({
  children,
  renderContainer = true,
}: NotificationProviderProps) => {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);

  const generateId = () => {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  };

  const showNotification = (
    type: NotificationType,
    message: string | JSX.Element,
    duration = 2000,
    slotId?: 1 | 2
  ) => {
    const id = generateId();
    const notification: NotificationItem = {
      id,
      type,
      message,
      duration,
      slotId,
    };

    setNotifications((prev) => [...prev, notification]);
  };

  const showError = (message: string, duration = 3000) => {
    showNotification("error", message, duration);
  };

  const showSuccess = (
    message: string | JSX.Element,
    duration = 3000,
    slotId?: 1 | 2
  ) => {
    showNotification("success", message, duration, slotId);
  };

  const showInfo = (message: string, duration = 3000) => {
    showNotification("info", message, duration);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
  };

  const clearAll = () => {
    setNotifications([]);
  };

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        showNotification,
        showError,
        showSuccess,
        showInfo,
        removeNotification,
        clearAll,
      }}
    >
      {children}
      {renderContainer && (
        <NotificationContainer
          notifications={notifications}
          onRemove={removeNotification}
        />
      )}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotification must be used within a NotificationProvider"
    );
  }
  return context;
};
